import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';

import { UserCircleIcon } from '@ag1/ui';
import { menuNavigationEvent, navigationClickEvent } from 'analytics/events/studio';
import { useAnalyticsHashedEmail } from 'analytics/hashed-email';

import { useSessionValue } from 'appLayer/auth';
import { useMedia } from 'state/context/MediaContext';
import { HeaderResult, HeaderSection } from 'types/studio/header';
import { HeaderFullV2 } from 'types/studio/studio';

import { LocaleEnum } from 'schema/locale';
import { PageTranslation } from 'schema/studio/studio';

import RegionSelectorModal from 'shared/components/RegionSelectorModal/RegionSelectorModal';
import StoreIcon from 'shared/components/StoreIcon/StoreIcon';
import { useLocale } from 'shared/hooks/useLocale';
import { IconBurger } from 'shared/ui/Icon/24/IconBurger';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';

import { routes } from 'constants/routes';

import { useScrollPaddingTopHandler } from '@portal/atoms/scrollPaddingTopAtom';
import { usePageContext } from '@portal/processes/Page/PageContext';
import { useStickyHeader } from '@portal/shared/ui/Nav/hooks/useStickyHeader';
import ArticleProgressBar from 'shared/studio/ArticleBody/ArticleProgressBar/ArticleProgressBar';
import { Banner, BannerType } from './Banner/Banner';
import styles from './HeaderV1.module.scss';
import HeaderLogo from './Logo/Logo';
import MarketingLinks from './MarketingLinks/MarketingLinks';
import HeaderMenuLink from './Navigation/LinkElement/LinkElement';
import Navigation from './Navigation/Navigation';

type Props = {
  className?: string;
  header: HeaderResult;
  logoSVG?: string;
  translations?: PageTranslation[];
  theme?: HeaderSection['theme'];
  hasProgressBar?: boolean;
  newThemeProgressBar?: boolean;
  renderCartButton?: (props: { theme?: HeaderSection['theme'] }) => React.ReactNode;
} & Omit<HeaderFullV2, '_type'>;

export const HeaderV1 = ({
  desktopLayout = 'transparentPrimary',
  mobileLayout = 'transparentPrimary',
  hasDifferentButtons,
  marketingButton,
  logoLink,
  shopLink,
  className,
  header,
  translations,
  logoSVG,
  theme,
  newThemeProgressBar,
  hasProgressBar,
  banner,
  renderCartButton
}: Props) => {
  useAnalyticsHashedEmail();
  const locale = useLocale();
  const { sectionsUsed } = usePageContext();

  const headerRef = useRef<HTMLElement>(null);
  const session = useSessionValue();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const customer = isLoggedIn && session?.customer;

  const [opened, setOpened] = useState(false);
  const [regionSelector, setRegionSelector] = useState(false);
  const { atSmall } = useMedia();
  const handleCloseMenu = () => {
    setOpened(false);
  };

  const handleOpenMenu = () => {
    setOpened(true);
    menuNavigationEvent({ location: 'header_burger_menu' });
  };

  useEffect(() => {
    if (session?.customer) setIsLoggedIn(!!session?.customer);
  }, [session?.customer]);

  const { sticky, visible } = useStickyHeader(headerRef, atSmall);

  useEffect(() => {
    if (opened) {
      document.body.classList.add(styles.scrollLock);
    } else {
      document.body.classList.remove(styles.scrollLock);
    }

    return () => {
      document.body.classList.remove(styles.scrollLock);
    };
  }, [opened]);

  const sendNavigationClickEvent = (location: string) => {
    setOpened(false);
    navigationClickEvent({ location, navigationType: 'link' });
  };

  const closeRegionSelector = () => {
    setRegionSelector(false);
  };

  const openRegionSelector = () => {
    setRegionSelector(true);
  };

  const headerLogoLink =
    hasDifferentButtons && logoLink?.length ? logoLink : header?.logo?.link || '/';

  const headerMarketingButton = () =>
    hasDifferentButtons && marketingButton?.marketingLink
      ? marketingButton
      : header?.marketingButton;

  const isNavigation = sectionsUsed?.navigation || false;
  const headerType = isNavigation ? 'navigation' : 'header';
  const bannerType = `pre-${headerType}` as const;
  const useDynamicHeaderTop = visible || atSmall;
  const { height: headerHeight, top: headerTop } = useScrollPaddingTopHandler(
    {
      type: headerType,
      disable: !useDynamicHeaderTop,
      noDebounce: true
    },
    headerRef
  );

  const placeholderStyles = useMemo(() => {
    if (!headerHeight) {
      return {};
    }
    return {
      height: `${headerHeight}px`
    } satisfies React.CSSProperties;
  }, [headerHeight]);

  return (
    <>
      {banner?.hasBanner && (
        <Banner
          banner={{ ...banner, countries: [] }}
          bannerType={BannerType.AG1_HAT_D3}
          scrollPaddingHeaderType={bannerType}
          asStatic={isNavigation}
        />
      )}
      <header
        className={clsx(
          styles.header,
          className,
          styles[`${desktopLayout}Desktop`],
          styles[`${mobileLayout}Mobile`],
          sticky && styles.sticky,
          visible && styles.visible,
          banner?.hasBanner && styles.banner,
          getThemeCSSClass(theme),
          isNavigation && styles.headerGroupItem
        )}
        ref={headerRef}
        style={headerTop && !isNavigation ? { top: headerTop } : undefined}>
        {!atSmall && hasProgressBar && (
          <ArticleProgressBar
            sticky={!visible && !atSmall}
            newThemeProgressBar={newThemeProgressBar}
          />
        )}
        <div className={styles.buttonsWrapper}>
          <button
            type="button"
            className={clsx(styles.burgerButton, getThemeCSSClass(theme))}
            onClick={handleOpenMenu}>
            <IconBurger className={styles.burgerButton} />
          </button>
          <HeaderMenuLink
            href={headerLogoLink}
            className={clsx([styles.logoWrapper, { [styles.hasCartButton]: renderCartButton }])}
            onClick={() => sendNavigationClickEvent('header_logo')}>
            <HeaderLogo
              header={header}
              logoSVG={logoSVG}
              isWhite={desktopLayout === 'transparentWhite' && !sticky}
            />
          </HeaderMenuLink>
          <HeaderMenuLink
            href={headerLogoLink}
            className={clsx(styles.logoMobileWrapper, styles.logoWrapper, {
              [styles.hasCartButton]: renderCartButton
            })}
            onClick={() => sendNavigationClickEvent('header_logo')}>
            <HeaderLogo
              header={header}
              logoSVG={logoSVG}
              isWhite={mobileLayout === 'transparentWhite' && !sticky}
            />
          </HeaderMenuLink>
        </div>
        <div
          className={clsx([
            styles.buttonsWrapper,
            styles.rightButtonsWrapper,
            { [styles.hasCartButton]: renderCartButton }
          ])}>
          {renderCartButton ? (
            renderCartButton({ theme })
          ) : (
            <MarketingLinks
              sticky={sticky}
              marketingButton={headerMarketingButton()}
              desktopLayout={desktopLayout}
              mobileLayout={mobileLayout}
              theme={theme}
            />
          )}

          {locale !== LocaleEnum.enUS && (
            <button
              className={clsx(styles.userIcon, styles.region)}
              type="button"
              onClick={openRegionSelector}>
              <StoreIcon circle />
            </button>
          )}

          <HeaderMenuLink
            href={routes.root}
            className={clsx(
              customer && styles.userButton,
              !customer && styles.userIcon,
              getThemeCSSClass(theme)
            )}
            onClick={() => sendNavigationClickEvent('header_account')}>
            {customer ? customer.firstName : <UserCircleIcon />}
          </HeaderMenuLink>
        </div>
        {hasProgressBar && atSmall && (
          <ArticleProgressBar newThemeProgressBar={newThemeProgressBar} />
        )}
      </header>
      {opened && (
        <Navigation
          header={header}
          logoLink={headerLogoLink}
          opened={opened}
          shopLink={hasDifferentButtons && shopLink?.navLink ? shopLink : null}
          closeMenu={handleCloseMenu}
          logoSVG={logoSVG}
          theme={theme}
          parentHeaderType={headerType}
        />
      )}
      <RegionSelectorModal
        isOpen={regionSelector}
        onClose={closeRegionSelector}
        pageTranslations={translations}
        text={header.regionSelector}
      />
      {!isNavigation && (
        <>
          {desktopLayout === 'white' && (
            <div
              className={clsx(styles.headerSpace, styles.headerSpaceDesktop)}
              style={placeholderStyles}
            />
          )}
          {mobileLayout === 'white' && (
            <div
              className={clsx(styles.headerSpace, styles.headerSpaceMobile)}
              style={placeholderStyles}
            />
          )}
        </>
      )}
    </>
  );
};

export default HeaderV1;
